import { useEffect, useContext } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { ExperienceTrackerContext } from '@confluence/experience-tracker';

import type { PresenceContentMode, PresenceContentType } from '../presenceTypes';

// eslint-disable-next-line import/no-cycle
import { ChangeboardingState } from './PresenceChangeboarding';

type UseChangeboardingEventsProps = {
	contentId: string;
	contentMode: PresenceContentMode;
	contentType: PresenceContentType;
	analyticsSource: string;
	featureName: string;
	experienceName: string;
	state: ChangeboardingState;
	error?: Error;
};

// Handles shared analytics and experience tracking events for changeboarding
export const useChangeboardingSharedEvents = ({
	contentId,
	contentMode,
	contentType,
	analyticsSource,
	experienceName,
	featureName,
	state,
	error,
}: UseChangeboardingEventsProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const experienceTracker = useContext(ExperienceTrackerContext);

	useEffect(() => {
		if (state === ChangeboardingState.STARTED) {
			experienceTracker.start({
				name: experienceName,
				attributes: {
					contentId,
					contentMode,
					contentType,
					changeboardingFeature: featureName,
				},
			});
		}

		if (state === ChangeboardingState.READY) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					source: analyticsSource,
					actionSubject: 'PresenceAvatarGroup',
					action: 'pulse shown',
					attributes: {
						contentId,
						contentMode,
						contentType,
						changeboardingFeature: featureName,
					},
				},
			}).fire();
		}

		if (state === ChangeboardingState.ACTIVE) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					source: analyticsSource,
					actionSubject: 'PresenceAvatarGroup',
					action: 'spotlight shown',
					attributes: {
						contentId,
						contentMode,
						contentType,
						changeboardingFeature: featureName,
					},
				},
			}).fire();
		}

		if (state === ChangeboardingState.COMPLETED) {
			experienceTracker.succeed({
				name: experienceName,
			});
		}

		if (state === ChangeboardingState.FAILED && error) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					source: analyticsSource,
					actionSubject: 'PresenceAvatarGroup',
					action: 'pulse failed',
					attributes: {
						contentId,
						contentMode,
						contentType,
						changeboardingFeature: featureName,
					},
				},
			}).fire();

			experienceTracker.fail({
				name: experienceName,
				error,
			});
		}

		if (state === ChangeboardingState.ABORTED) {
			experienceTracker.abort({
				name: experienceName,
				reason: 'Changeboarding done',
			});
		}
	}, [
		state,
		featureName,
		error,
		contentId,
		contentMode,
		contentType,
		createAnalyticsEvent,
		analyticsSource,
		experienceTracker,
		experienceName,
	]);

	useEffect(() => {
		return () => {
			experienceTracker.abort({
				name: experienceName,
				reason: 'Changeboarding unmounted',
			});
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
};
